import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Activity, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  RefreshCw,
  Loader2,
  Globe,
  Server,
  Clock,
  Link as LinkIcon,
  ExternalLink
} from 'lucide-react';
import { API } from '../config/api';
import { ALL_ROUTES, CRITICAL_API_ENDPOINTS } from '../utils/routeAudit';

export default function AdminRouteAuditPage() {
  const { user, isOwner, getAuthHeaders, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [apiResults, setApiResults] = useState(null);
  const [routeResults, setRouteResults] = useState(null);
  const [activeTab, setActiveTab] = useState('api');

  useEffect(() => {
    if (!isOwner && user?.role !== 'admin') {
      navigate('/');
    }
  }, [isOwner, user, navigate]);

  const runApiAudit = async () => {
    setLoading(true);
    const results = {
      total: CRITICAL_API_ENDPOINTS.length,
      passed: 0,
      failed: 0,
      errors: [],
      details: [],
      timestamp: new Date().toISOString(),
    };

    for (const endpoint of CRITICAL_API_ENDPOINTS) {
      const startTime = Date.now();
      const result = {
        endpoint: endpoint.path,
        name: endpoint.name,
        method: endpoint.method,
        auth: endpoint.auth,
        status: null,
        ok: false,
        error: null,
        responseTime: 0,
      };

      try {
        const headers = { 'Content-Type': 'application/json' };
        if (token && endpoint.auth) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const apiPath = endpoint.path.replace('/api', '');
        const response = await fetch(`${API}${apiPath}`, {
          method: endpoint.method,
          headers,
          credentials: 'include',
        });

        result.status = response.status;
        result.responseTime = Date.now() - startTime;
        
        // 401 for auth endpoints without token is expected
        if (response.ok || (response.status === 401 && endpoint.auth && !token)) {
          result.ok = true;
          results.passed++;
        } else {
          try {
            const data = await response.json();
            result.error = data.detail || data.message || `HTTP ${response.status}`;
          } catch {
            result.error = `HTTP ${response.status}`;
          }
          results.failed++;
          results.errors.push(result);
        }
      } catch (error) {
        result.responseTime = Date.now() - startTime;
        result.error = error.message || 'Network error';
        results.failed++;
        results.errors.push(result);
      }

      results.details.push(result);
    }

    setApiResults(results);
    setLoading(false);
    
    if (results.failed === 0) {
      toast.success(`All ${results.total} API endpoints passed!`);
    } else {
      toast.error(`${results.failed} of ${results.total} API endpoints failed`);
    }
  };

  const runRouteAudit = async () => {
    setLoading(true);
    const results = {
      total: ALL_ROUTES.length,
      passed: 0,
      failed: 0,
      warnings: 0,
      errors: [],
      details: [],
      timestamp: new Date().toISOString(),
    };

    for (const route of ALL_ROUTES) {
      const result = {
        path: route.path,
        name: route.name,
        auth: route.auth,
        role: route.role,
        ok: true,
        warning: null,
        error: null,
      };

      // Routes that require auth or specific roles get warnings if testing without that context
      if (route.auth && !token) {
        result.warning = 'Requires authentication';
        results.warnings++;
      } else if (route.role && route.role !== user?.role && !isOwner) {
        result.warning = `Requires ${route.role} role`;
        results.warnings++;
      }

      // All defined routes are valid since they're in App.js
      results.passed++;
      results.details.push(result);
    }

    setRouteResults(results);
    setLoading(false);
    toast.success(`Route audit complete: ${results.passed} routes defined`);
  };

  const runFullAudit = async () => {
    await runApiAudit();
    await runRouteAudit();
  };

  const getStatusBadge = (result) => {
    if (result.ok) {
      return <Badge className="bg-green-500/20 text-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Pass</Badge>;
    }
    if (result.warning) {
      return <Badge className="bg-amber-500/20 text-amber-500"><AlertTriangle className="w-3 h-3 mr-1" /> Warn</Badge>;
    }
    return <Badge className="bg-red-500/20 text-red-500"><XCircle className="w-3 h-3 mr-1" /> Fail</Badge>;
  };

  return (
    <div className="min-h-screen bg-background p-6" data-testid="admin-route-audit-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase flex items-center gap-3">
              <Activity className="w-8 h-8 text-primary" />
              Route & API Audit
            </h1>
            <p className="text-muted-foreground mt-1">
              Scan for broken routes, missing handlers, and dead buttons
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={runFullAudit}
              disabled={loading}
              className="bg-primary"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Run Full Audit
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="glass-card border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Server className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">API Endpoints</p>
                <p className="text-2xl font-bold">{CRITICAL_API_ENDPOINTS.length}</p>
              </div>
            </div>
          </Card>
          
          <Card className="glass-card border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Globe className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Frontend Routes</p>
                <p className="text-2xl font-bold">{ALL_ROUTES.length}</p>
              </div>
            </div>
          </Card>
          
          <Card className="glass-card border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Passed</p>
                <p className="text-2xl font-bold text-green-500">
                  {(apiResults?.passed || 0) + (routeResults?.passed || 0)}
                </p>
              </div>
            </div>
          </Card>
          
          <Card className="glass-card border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Failed</p>
                <p className="text-2xl font-bold text-red-500">
                  {(apiResults?.failed || 0) + (routeResults?.failed || 0)}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={activeTab === 'api' ? 'default' : 'outline'}
            onClick={() => setActiveTab('api')}
            className={activeTab === 'api' ? 'bg-primary' : ''}
          >
            <Server className="w-4 h-4 mr-2" />
            API Endpoints
          </Button>
          <Button
            variant={activeTab === 'routes' ? 'default' : 'outline'}
            onClick={() => setActiveTab('routes')}
            className={activeTab === 'routes' ? 'bg-primary' : ''}
          >
            <Globe className="w-4 h-4 mr-2" />
            Frontend Routes
          </Button>
        </div>

        {/* API Results */}
        {activeTab === 'api' && (
          <Card className="glass-card border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold uppercase">API Endpoint Status</h2>
              <Button onClick={runApiAudit} variant="outline" size="sm" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
            
            {apiResults ? (
              <div className="space-y-2">
                {apiResults.details.map((result, i) => (
                  <div 
                    key={i}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      result.ok ? 'bg-green-500/5' : 'bg-red-500/5'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {getStatusBadge(result)}
                      <div>
                        <p className="font-medium text-sm">{result.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {result.method} {result.endpoint}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      {result.status && (
                        <span className={result.ok ? 'text-green-500' : 'text-red-500'}>
                          {result.status}
                        </span>
                      )}
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {result.responseTime}ms
                      </span>
                      {result.error && (
                        <span className="text-red-400 text-xs max-w-[200px] truncate">
                          {result.error}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Server className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Click "Run Full Audit" to test API endpoints</p>
              </div>
            )}
          </Card>
        )}

        {/* Route Results */}
        {activeTab === 'routes' && (
          <Card className="glass-card border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold uppercase">Frontend Route Status</h2>
              <Button onClick={runRouteAudit} variant="outline" size="sm" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
            
            {routeResults ? (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {routeResults.details.map((result, i) => (
                  <div 
                    key={i}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      result.warning ? 'bg-amber-500/5' : 'bg-green-500/5'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {result.warning ? (
                        <Badge className="bg-amber-500/20 text-amber-500">
                          <AlertTriangle className="w-3 h-3 mr-1" /> Auth
                        </Badge>
                      ) : (
                        <Badge className="bg-green-500/20 text-green-500">
                          <CheckCircle className="w-3 h-3 mr-1" /> OK
                        </Badge>
                      )}
                      <div>
                        <p className="font-medium text-sm">{result.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{result.path}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {result.auth && (
                        <Badge variant="outline" className="text-xs">Auth Required</Badge>
                      )}
                      {result.role && (
                        <Badge variant="outline" className="text-xs">{result.role}</Badge>
                      )}
                      <a 
                        href={result.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Globe className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Click "Run Full Audit" to check frontend routes</p>
              </div>
            )}
          </Card>
        )}

        {/* Failed Endpoints Summary */}
        {apiResults?.errors?.length > 0 && (
          <Card className="glass-card border-red-500/30 p-6 mt-6">
            <h2 className="font-heading text-xl font-bold uppercase text-red-500 mb-4 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Failed Endpoints ({apiResults.errors.length})
            </h2>
            <div className="space-y-3">
              {apiResults.errors.map((error, i) => (
                <div key={i} className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{error.name}</p>
                      <p className="text-sm text-muted-foreground font-mono">
                        {error.method} {error.endpoint}
                      </p>
                    </div>
                    <Badge className="bg-red-500/20 text-red-500">
                      {error.status || 'Error'}
                    </Badge>
                  </div>
                  {error.error && (
                    <p className="mt-2 text-sm text-red-400">{error.error}</p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
